import { useState, useCallback } from 'react';

/**
 * Hook to handle mention search logic in text inputs.
 * Scans for a leading '@' followed by characters at the current cursor position.
 */
export const useMentionSearch = () => {
    const [mentionQuery, setMentionQuery] = useState<string | null>(null);
    const [cursorPosition, setCursorPosition] = useState<number>(0);

    const updateSearch = useCallback((text: string, selectionStart: number) => {
        setCursorPosition(selectionStart);
        
        // Look back from cursor to find a leading @
        // Must be either at start of string or preceded by a space
        const textBeforeCursor = text.substring(0, selectionStart);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
            
            // Check if @ is at start or preceded by space
            if (charBeforeAt === ' ' || charBeforeAt === '\n') {
                const query = textBeforeCursor.substring(lastAtIndex + 1);
                
                // Query shouldn't contain spaces (as mentions are single words)
                if (!query.includes(' ')) {
                    setMentionQuery(query);
                    return;
                }
            }
        }
        
        setMentionQuery(null);
    }, []);

    const completeMention = useCallback((text: string, username: string, startPos: number) => {
        const textBeforeAt = text.substring(0, text.substring(0, startPos).lastIndexOf('@'));
        const textAfterCursor = text.substring(startPos);
        const newText = `${textBeforeAt}@${username} ${textAfterCursor}`;
        const newCursorPos = textBeforeAt.length + username.length + 2; // +1 for @, +1 for space
        
        setMentionQuery(null);
        return { newText, newCursorPos };
    }, []);

    return {
        mentionQuery,
        setMentionQuery,
        updateSearch,
        completeMention
    };
};
